/**
 * routes.ts — Mapa centralizado de rotas da API para o frontend
 * 
 * Substitui strings hardcoded por constantes tipadas.
 * Usa template functions para rotas com parametros.
 * 
 * @example
 * ```ts
 * import { ROUTES } from '../api/routes';
 * 
 * // Antes: api.get(`/escolas/${escolaId}/alunos`)
 * // Depois: api.get(ROUTES.alunos.listar(escolaId))
 * ```
 */

// ═══════════════════════════════════════════════
// AUTH
// ═══════════════════════════════════════════════
export const AUTH = {
  login: '/auth/login',
  loginGoogle: '/auth/login-google',
  checkEmail: '/auth/check-email',
  accountLookup: {
    byCpfBirth: '/auth/account-lookup/by-cpf-birth',
  },
  verifyToken: '/auth/verify-token',
  activate: '/auth/activate',
  activateByToken: '/auth/activate-by-token',
  quizPerguntas: '/auth/quiz-perguntas',
  quizVerificar: '/auth/quiz-verificar',
  sendEmailCode: '/auth/send-email-code',
  verifyEmailCode: '/auth/verify-email-code',
  activateDirect: '/auth/activate-direct',
  me: '/auth/me',
  mePassword: '/auth/me/password',
  mePreferencias: '/auth/me/preferencias',
  pushRegister: '/auth/push/register',
  qrLink: '/auth/qr-link',
  activeSession: '/auth/active-session',
  linkStatus: (sessionId: string) => `/auth/link-status/${encodeURIComponent(sessionId)}`,
  sessionDesktopDisconnect: (sessionId: string) =>
    `/auth/session/${encodeURIComponent(sessionId)}/desktop-disconnect`,
  refresh: '/auth/refresh',
  completeSetup: '/auth/complete-setup',
  resendInvite: (userId: string) => `/auth/resend-invite/${userId}`,
  passwordRecovery: {
    request: '/auth/password-recovery/request',
    verify: '/auth/password-recovery/verify',
    reset: '/auth/password-recovery/reset',
  },
} as const;

// ═══════════════════════════════════════════════
// ESCOLAS
// ═══════════════════════════════════════════════
export const ESCOLAS = {
  listar: '/escolas',
  findOne: (escolaId: string) => `/escolas/${escolaId}`,
  dashboard: (escolaId: string) => `/escolas/${escolaId}/dashboard`,
  publicInfo: (escolaId: string) => `/escolas/${escolaId}/public`,
  bootstrapStatus: (escolaId: string) => `/escolas/${escolaId}/bootstrap-status`,
  dadosExemploStatus: (escolaId: string) => `/escolas/${escolaId}/dados-exemplo-status`,
  limparDadosExemplo: (escolaId: string) => `/escolas/${escolaId}/limpar-dados-exemplo`,
  checkInep: (inep: string, excludeId?: string) =>
    `/escolas/check-inep?inep=${encodeURIComponent(inep)}${excludeId ? `&excludeId=${encodeURIComponent(excludeId)}` : ''}`,
} as const;

// ═══════════════════════════════════════════════
// ALUNOS
// ═══════════════════════════════════════════════
export const ALUNOS = {
  listar: (escolaId: string) => `/escolas/${escolaId}/alunos`,
  /** Alunos com vínculo de responsáveis (cadastro / dashboard equipe). */
  comResponsaveis: (escolaId: string) => `/escolas/${escolaId}/alunos/alunos-responsaveis`,
  findOne: (escolaId: string, alunoId: string) => `/escolas/${escolaId}/alunos/${alunoId}`,
  criar: (escolaId: string) => `/escolas/${escolaId}/alunos`,
  atualizar: (escolaId: string, alunoId: string) => `/escolas/${escolaId}/alunos/${alunoId}`,
} as const;

// ═══════════════════════════════════════════════
// TURMAS
// ═══════════════════════════════════════════════
export const TURMAS = {
  listar: (escolaId: string) => `/escolas/${escolaId}/turmas`,
  findOne: (escolaId: string, turmaId: string) => `/escolas/${escolaId}/turmas/${turmaId}`,
  criar: (escolaId: string) => `/escolas/${escolaId}/turmas`,
  batch: (escolaId: string) => `/escolas/${escolaId}/turmas/batch`,
  atualizar: (escolaId: string, turmaId: string) => `/escolas/${escolaId}/turmas/${turmaId}`,
  remover: (escolaId: string, turmaId: string) => `/escolas/${escolaId}/turmas/${turmaId}`,
  hardDelete: (escolaId: string, turmaId: string) => `/escolas/${escolaId}/turmas/hard/${turmaId}`,
} as const;

// ═══════════════════════════════════════════════
// PROFESSORES
// ═══════════════════════════════════════════════
export const PROFESSORES = {
  listar: (escolaId: string) => `/escolas/${escolaId}/professores`,
  findOne: (escolaId: string, profId: string) => `/escolas/${escolaId}/professores/${profId}`,
  materias: (escolaId: string, profId: string) => `/escolas/${escolaId}/professores/${profId}/materias`,
  removerMateria: (escolaId: string, profId: string, vinculoId: string) =>
    `/escolas/${escolaId}/professores/${profId}/materias/${vinculoId}`,
} as const;

// ═══════════════════════════════════════════════
// RESPONSAVEIS
// ═══════════════════════════════════════════════
export const RESPONSAVEIS = {
  listar: (escolaId: string) => `/escolas/${escolaId}/responsaveis`,
  findOne: (escolaId: string, respId: string) => `/escolas/${escolaId}/responsaveis/${respId}`,
  reativar: (escolaId: string, respId: string) => `/escolas/${escolaId}/responsaveis/${respId}/reativar`,
} as const;

// ═══════════════════════════════════════════════
// HORARIOS
// ═══════════════════════════════════════════════
export const HORARIOS = {
  porTurma: (escolaId: string, turmaId: string) => `/escolas/${escolaId}/turmas/${turmaId}/horarios`,
  gerar: (escolaId: string, turmaId: string) => `/escolas/${escolaId}/turmas/${turmaId}/horarios/gerar`,
  redefinir: (escolaId: string, turmaId: string) => `/escolas/${escolaId}/turmas/${turmaId}/horarios/redefinir`,
  bulk: (escolaId: string, turmaId: string) => `/escolas/${escolaId}/turmas/${turmaId}/horarios/bulk`,
  porEscola: (escolaId: string) => `/escolas/${escolaId}/horarios`,
  ocupacao: (escolaId: string) => `/escolas/${escolaId}/horarios/ocupacao`,
  template: (escolaId: string, turnoId: string) => `/escolas/${escolaId}/horarios/template/${turnoId}`,
} as const;

// ═══════════════════════════════════════════════
// EQUIPE
// ═══════════════════════════════════════════════
export const EQUIPE = {
  listar: (escolaId: string) => `/escolas/${escolaId}/equipe`,
  findOne: (escolaId: string, membroId: string) => `/escolas/${escolaId}/equipe/${membroId}`,
  convidar: (escolaId: string) => `/escolas/${escolaId}/equipe/convidar`,
  reenviar: (escolaId: string, membroId: string) => `/escolas/${escolaId}/equipe/${membroId}/reenviar`,
  status: (escolaId: string, membroId: string) => `/escolas/${escolaId}/equipe/${membroId}/status`,
} as const;

// ═══════════════════════════════════════════════
// MATERIAS e TURNOS
// ═══════════════════════════════════════════════
export const MATERIAS = {
  listar: (escolaId: string) => `/escolas/${escolaId}/materias`,
} as const;

export const TURNOS = {
  listar: (escolaId: string) => `/escolas/${escolaId}/turnos`,
} as const;

// ═══════════════════════════════════════════════
// ADMIN (hierarquia)
// ═══════════════════════════════════════════════
export const ADMIN = {
  redes: '/admin/redes',
  estados: '/admin/estados',
  estadosPorRede: (redeId: string) => `/admin/estados?redeId=${redeId}`,
  municipios: '/admin/municipios',
  municipiosPorEstado: (estadoId: string) => `/admin/municipios?estadoId=${estadoId}`,
  usuarios: '/admin/usuarios',
} as const;

// ═══════════════════════════════════════════════
// UPLOAD
// ═══════════════════════════════════════════════
export const UPLOAD = {
  fotoAluno: (id: string) => `/upload/foto-aluno/${id}`,
  fotoUsuario: (id: string) => `/upload/foto-usuario/${id}`,
  fotoResponsavel: (id: string) => `/upload/foto-responsavel/${id}`,
} as const;

// ═══════════════════════════════════════════════
// VALIDACOES
// ═══════════════════════════════════════════════
export const VALIDACOES = {
  cpf: (cpf: string) => `/validacoes/cpf/${cpf}`,
  email: (email: string) => `/validacoes/email/${encodeURIComponent(email)}`,
} as const;

// ═══════════════════════════════════════════════
// PUBLICACOES / TV
// ═══════════════════════════════════════════════
export const PUBLICACOES = {
  gestao: '/publicacoes/gestao',
  tv: {
    canais: '/publicacoes/tv/canais',
    ensurePrioritario: '/publicacoes/tv/canais/ensure-prioritario',
    grade: (canalId: string) => `/publicacoes/tv/grade/${canalId}`,
    gradeBase: '/publicacoes/tv/grade',
  },
} as const;

// ═══════════════════════════════════════════════
// SECURITY
// ═══════════════════════════════════════════════
export const SECURITY = {
  incidentesEscola: (escolaId: string, dias = 30, limite = 50) =>
    `/security/incidentes-escola?escolaId=${escolaId}&dias=${dias}&limite=${limite}`,
  incidenteAcao: (id: string, tipo: string) => `/auth/security/incidentes/${id}/${tipo}`,
  dispositivos: '/auth/security/dispositivos',
  dispositivo: (id: string) => `/auth/security/dispositivos/${encodeURIComponent(id)}`,
  historico: (limite = 30) => `/auth/security/historico?limite=${limite}`,
  config: '/auth/security/config',
  approval: {
    handoff: (id: string) => `/auth/security/approval/${encodeURIComponent(id)}/handoff`,
    handoffConsume: (id: string) => `/auth/security/approval/${encodeURIComponent(id)}/handoff/consume`,
    status: (id: string) => `/auth/security/approval/status/${encodeURIComponent(id)}`,
    responder: '/auth/security/approval/responder',
    deviceRegister: '/auth/security/approval/device/register',
    solicitarEmail: '/auth/security/approval/solicitar-email',
    verificarEmail: '/auth/security/approval/verificar-email',
    solicitarSuperior: '/auth/security/approval/solicitar-superior',
    responderTeste: '/auth/security/approval/responder-teste',
    pendentes: '/auth/security/approval/pendentes',
    preferencias: '/auth/security/approval/preferencias',
  },
} as const;

// ═══════════════════════════════════════════════
// CATALOGO ESCOLAS
// ═══════════════════════════════════════════════
export const CATALOGO = {
  busca: (q: string, limit = 50) => `/catalogo-escolas/busca?q=${encodeURIComponent(q)}&limit=${limit}`,
  area: '/catalogo-escolas/area',
  clusters: '/catalogo-escolas/clusters',
  clustersStatus: '/catalogo-escolas/clusters/status',
  gerarClusters: '/catalogo-escolas/gerar-clusters',
} as const;

// ═══════════════════════════════════════════════
// INTEL MUNICIPAL
// ═══════════════════════════════════════════════
export const INTEL_MUNICIPAL = {
  resumoUf: '/intel-municipal/resumo-uf',
  ranking: (uf: string, limit = 1000) => `/intel-municipal/ranking?uf=${uf}&limit=${limit}`,
  detalhe: (codIbge: string) => `/intel-municipal/${codIbge}`,
} as const;

// ═══════════════════════════════════════════════
// MOBILE / RESPONSAVEL
// ═══════════════════════════════════════════════
export const MOBILE_RESPONSAVEL = {
  resumo: (respId: string) => `/mobile/responsaveis/${respId}/resumo`,
  statusHoje: (respId: string) => `/mobile/responsaveis/${respId}/status-hoje`,
  timeline: (respId: string, limite = 20) => `/mobile/responsaveis/${respId}/timeline?limite=${limite}`,
  desempenho: (respId: string) => `/mobile/responsaveis/${respId}/desempenho`,
  frequencia: (respId: string, alunoId?: string, dataInicio?: string, dataFim?: string) => {
    let url = `/mobile/responsaveis/${respId}/frequencia`;
    const params = new URLSearchParams();
    if (alunoId) params.set('alunoId', alunoId);
    if (dataInicio) params.set('dataInicio', dataInicio);
    if (dataFim) params.set('dataFim', dataFim);
    const qs = params.toString();
    return qs ? `${url}?${qs}` : url;
  },
  justificativas: (respId: string) => `/mobile/responsaveis/${respId}/justificativas`,
  comunicados: (respId: string) => `/mobile/responsaveis/${respId}/comunicados`,
  escola: (respId: string) => `/mobile/responsaveis/${respId}/escola`,
  observacoes: (respId: string) => `/mobile/responsaveis/${respId}/observacoes`,
  notificacaoLida: (respId: string, notId: string) =>
    `/mobile/responsaveis/${respId}/notificacoes/${notId}/lida`,
} as const;

// ═══════════════════════════════════════════════
// PROFESSOR
// ═══════════════════════════════════════════════
export const PROFESSOR = {
  onboardingConcluir: '/professor/onboarding/concluir',
  atividadesAuxiliares: {
    registros: (data: string) => `/professor/atividades-auxiliares/registros?data=${data}`,
    alternarConclusao: (id: string, data: string) =>
      `/professor/atividades-auxiliares/${id}/alternar-conclusao?data=${data}`,
  },
} as const;

// ═══════════════════════════════════════════════
// RELATORIOS
// ═══════════════════════════════════════════════
export const RELATORIOS = {
  consolidacao: (escolaId: string) => `/relatorios/consolidacao/${escolaId}`,
  frequencia: (escolaId: string, turmaId: string) => `/relatorios/frequencia/${escolaId}/${turmaId}`,
} as const;

// ═══════════════════════════════════════════════
// PRESENCAS
// ═══════════════════════════════════════════════
export const PRESENCAS = {
  diarias: (escolaId: string, params: { alunoId?: string; dataInicio?: string; dataFim?: string; limite?: number }) => {
    const qs = new URLSearchParams();
    if (params.alunoId) qs.set('alunoId', params.alunoId);
    if (params.dataInicio) qs.set('dataInicio', params.dataInicio);
    if (params.dataFim) qs.set('dataFim', params.dataFim);
    if (params.limite) qs.set('limite', String(params.limite));
    return `/escolas/${escolaId}/presencas-diarias?${qs.toString()}`;
  },
} as const;

// ═══════════════════════════════════════════════
// DISPOSITIVOS LEITURA
// ═══════════════════════════════════════════════
export const DISPOSITIVOS_LEITURA = {
  remover: (escolaId: string, id: string) => `/escolas/${escolaId}/dispositivos-leitura/${id}`,
} as const;

// ═══════════════════════════════════════════════
// INCIDENTES OPERACIONAIS
// ═══════════════════════════════════════════════
export const INCIDENTES_OP = {
  resolver: (escolaId: string, id: string) => `/escolas/${escolaId}/incidentes-operacionais/${id}/resolver`,
} as const;

// ═══════════════════════════════════════════════
// MASTER
// ═══════════════════════════════════════════════
export const MASTER = {
  crescimento: '/master/analise/crescimento',
  financeiro: '/master/analise/financeiro',
  indices: '/master/analise/indices',
} as const;

// ═══════════════════════════════════════════════
// MUNICIPAL CONFIG
// ═══════════════════════════════════════════════
export const MUNICIPAL = {
  configuracao: '/municipal/configuracao',
  midias: '/municipal/midias',
  midiaById: (id: string) => `/municipal/midias/${id}`,
} as const;

// ═══════════════════════════════════════════════
// DEV
// ═══════════════════════════════════════════════
export const DEV = {
  control: '/dev/control',
} as const;


// ═══════════════════════════════════════════════
// CHAMADAS
// ═══════════════════════════════════════════════
export const CHAMADAS = {
  listar: (escolaId: string) => `/escolas/${escolaId}/chamadas`,
  findOne: (escolaId: string, chamadaId: string) => `/escolas/${escolaId}/chamadas/${chamadaId}`,
  alunos: (escolaId: string, chamadaId: string) => `/escolas/${escolaId}/chamadas/${chamadaId}/alunos`,
  concluir: (escolaId: string, chamadaId: string) => `/escolas/${escolaId}/chamadas/${chamadaId}/concluir`,
  statusDia: (escolaId: string) => `/escolas/${escolaId}/chamadas/status-dia`,
} as const;

export const COBERTURA = {
  afastamentos: (escolaId: string) => `/escolas/${escolaId}/cobertura/afastamentos`,
  cancelarAfastamento: (escolaId: string, id: string) =>
    `/escolas/${escolaId}/cobertura/afastamentos/${id}/cancelar`,
  alertas: (escolaId: string) => `/escolas/${escolaId}/cobertura/alertas`,
  candidatos: (escolaId: string, alertaId: string) =>
    `/escolas/${escolaId}/cobertura/alertas/${alertaId}/candidatos`,
  alocar: (escolaId: string, alertaId: string) =>
    `/escolas/${escolaId}/cobertura/alertas/${alertaId}/alocar`,
  revogarAlerta: (escolaId: string, alertaId: string) =>
    `/escolas/${escolaId}/cobertura/alertas/${alertaId}/revogar`,
  alocarLote: (escolaId: string) => `/escolas/${escolaId}/cobertura/alertas/alocar-lote`,
  previewEncaixe: (escolaId: string) =>
    `/escolas/${escolaId}/cobertura/alertas/preview-encaixe`,
  rankingEncaixe: (escolaId: string) =>
    `/escolas/${escolaId}/cobertura/alertas/ranking-encaixe`,
  professorEfetivo: (escolaId: string) => `/escolas/${escolaId}/cobertura/professor-efetivo`,
} as const;

// ═══════════════════════════════════════════════
// DIARIOS DE CLASSE
// ═══════════════════════════════════════════════
export const DIARIOS_CLASSE = {
  base: '/diarios-classe',
} as const;

// ═══════════════════════════════════════════════
// OBSERVE (telemetria)
// ═══════════════════════════════════════════════
export const OBSERVE = {
  track: '/observe/track',
} as const;

// ═══════════════════════════════════════════════
// WIZARD
// ═══════════════════════════════════════════════
export const WIZARD = {
  progresso: '/wizard/progresso',
  salvarEtapa: '/wizard/salvar-etapa',
  avancarEtapa: '/wizard/avancar-etapa',
  confirmar: '/wizard/confirmar',
  reset: '/wizard/reset',
  status: '/wizard/status',
  resetEscola: '/wizard/reset-escola',
  progressoSSE: '/wizard/progresso-sse',
} as const;

// ═══════════════════════════════════════════════
// COMUNICADOS
// ═══════════════════════════════════════════════
export const COMUNICADOS = {
  porEscola: (escolaId: string) => `/escolas/${escolaId}/comunicados`,
} as const;

// ═══════════════════════════════════════════════
// INCIDENTES OPERACIONAIS
// ═══════════════════════════════════════════════
export const INCIDENTES = {
  porEscola: (escolaId: string) => `/escolas/${escolaId}/incidentes-operacionais`,
  resolver: (escolaId: string, id: string) => `/escolas/${escolaId}/incidentes-operacionais/${id}/resolver`,
} as const;

// ═══════════════════════════════════════════════
// BILLING
// ═══════════════════════════════════════════════
export const BILLING = {
  planos: '/billing/planos',
  assinaturas: '/billing/assinaturas',
  faturas: '/billing/faturas',
  stripe: {
    config: '/billing/stripe/config',
    webhook: '/billing/stripe/webhook',
  },
  nfse: {
    config: '/billing/nfse/config',
  },
} as const;

// ═══════════════════════════════════════════════
// TAGS RFID
// ═══════════════════════════════════════════════
export const TAGS_RFID = {
  porEscola: (escolaId: string) => `/escolas/${escolaId}/tags-rfid`,
  findOne: (escolaId: string, tagId: string) => `/escolas/${escolaId}/tags-rfid/${tagId}`,
  porAluno: (escolaId: string, alunoId: string) => `/escolas/${escolaId}/tags-rfid/aluno/${alunoId}`,
} as const;

// ═══════════════════════════════════════════════
// AVALIACOES
// ═══════════════════════════════════════════════
export const AVALIACOES = {
  porEscola: (escolaId: string) => `/escolas/${escolaId}/avaliacoes`,
  findOne: (escolaId: string, avalId: string) => `/escolas/${escolaId}/avaliacoes/${avalId}`,
  notas: (escolaId: string, avalId: string) => `/escolas/${escolaId}/avaliacoes/${avalId}/notas`,
} as const;

// ═══════════════════════════════════════════════
// OBSERVACOES
// ═══════════════════════════════════════════════
export const OBSERVACOES = {
  porEscola: (escolaId: string) => `/escolas/${escolaId}/observacoes`,
  findOne: (escolaId: string, obsId: string) => `/escolas/${escolaId}/observacoes/${obsId}`,
} as const;

// ═══════════════════════════════════════════════
// AUDIT
// ═══════════════════════════════════════════════
export const AUDIT = {
  logs: '/admin/audit',
  stats: '/admin/audit/stats',
} as const;

// ═══════════════════════════════════════════════
// BARREL — Exportacao unificada
// ═══════════════════════════════════════════════
export const ROUTES = {
  auth: AUTH,
  escolas: ESCOLAS,
  alunos: ALUNOS,
  turmas: TURMAS,
  professores: PROFESSORES,
  responsaveis: RESPONSAVEIS,
  horarios: HORARIOS,
  equipe: EQUIPE,
  materias: MATERIAS,
  turnos: TURNOS,
  admin: ADMIN,
  upload: UPLOAD,
  validacoes: VALIDACOES,
  publicacoes: PUBLICACOES,
  security: SECURITY,
  catalogo: CATALOGO,
  intelMunicipal: INTEL_MUNICIPAL,
  mobileResponsavel: MOBILE_RESPONSAVEL,
  professor: PROFESSOR,
  relatorios: RELATORIOS,
  presencas: PRESENCAS,
  dispositivosLeitura: DISPOSITIVOS_LEITURA,
  incidentesOp: INCIDENTES_OP,
  master: MASTER,
  municipal: MUNICIPAL,
  dev: DEV,
  chamadas: CHAMADAS,
  cobertura: COBERTURA,
  diariosClasse: DIARIOS_CLASSE,
  observe: OBSERVE,
  wizard: WIZARD,
  comunicados: COMUNICADOS,
  incidentes: INCIDENTES,
  billing: BILLING,
  tagsRfid: TAGS_RFID,
  avaliacoes: AVALIACOES,
  observacoes: OBSERVACOES,
  audit: AUDIT,
} as const;
